import { Link } from '@inertiajs/react';

import Icon from '@/components/public/ui/Icon';
import Seo from '@/components/public/Seo';
import PageHero from '@/components/public/sections/PageHero';
import { parishName, useSite } from '@/hooks/useSite';
import { trackUmamiEvent } from '@/lib/analytics';

const PDF_HREF = '/documents/transparency/plano-prevencao-riscos.pdf';

const sectionLabel =
  'text-xs font-bold tracking-widest uppercase text-primary \
  mb-3 block';

export default function RiskPreventionPlan() {
  const name = parishName(useSite());
  const pageBadge = 'Transparência';
  const pageTitle = 'Plano de Prevenção de Riscos.';
  const pageAccentWord = 'Prevenção.';
  const pageSubtitle =
    'Instrumento de gestão para a prevenção de riscos de \
    gestão, corrupção e infrações conexas, em cumprimento do Regime Geral de Prevenção da \
    Corrupção (Decreto-Lei n.º 109-E/2021, de 9 de dezembro).';

  const baseClassName = 'max-w-7xl mx-auto px-4 md:px-8 py-16';

  return (
    <>
      <Seo
        title="Plano de Prevenção de Riscos"
        description={`Plano de Prevenção de Riscos de Gestão, de Corrupção e Infrações Conexas da ${name}.`}
      />
      <PageHero
        badge={pageBadge}
        breadcrumbs={[
          { label: 'Início', href: '/' },
          { label: 'Transparência' },
          { label: 'Plano de Prevenção de Riscos' },
        ]}
        title={pageTitle}
        accentWord={pageAccentWord}
        subtitle={pageSubtitle}
      />
      <div className={baseClassName}>
        <div className="mx-auto max-w-4xl space-y-6">
          {/* Documento integral — quick-access CTA at the top of the page. */}
          <a
            href={PDF_HREF}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              trackUmamiEvent('document_download', {
                document_type: 'risk_prevention_plan',
                source: 'top_cta',
              })
            }
            className="surface-card text-on-surface group hover:border-line-strong flex items-center gap-4 rounded-xl p-5 transition-all hover:shadow-md md:p-6"
          >
            <span
              className="bg-primary/[0.12] flex h-12 w-12 shrink-0 items-center justify-center rounded-xl"
              aria-hidden="true"
            >
              <span className="text-2xl leading-none">📄</span>
            </span>
            <span className="min-w-0 flex-1">
              <span className="text-on-surface block text-base font-bold md:text-lg">
                Plano de Prevenção de Riscos: versão integral
              </span>
              <span className="text-on-surface-light block text-sm">
                PDF · 14 páginas · 236&nbsp;KB
              </span>
            </span>
            <span className="text-primary inline-flex items-center gap-1 text-sm font-semibold">
              Abrir
              <Icon name="arrow_forward" size="sm" />
            </span>
          </a>

          <article className="surface-card text-on-surface-light space-y-8 p-6 leading-relaxed md:p-10">
            <section>
              <span className={sectionLabel}>1 — Enquadramento Legal</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Base jurídica
              </h2>
              <p>
                O Plano de Prevenção de Riscos de Gestão, de Corrupção e Infrações Conexas é
                elaborado nos termos do Regime Geral de Prevenção da Corrupção (RGPC), aprovado em
                anexo ao Decreto-Lei n.º 109-E/2021, de 9 de dezembro, aplicável às autarquias
                locais por força do n.º 2 do artigo 2.º do mesmo diploma.
              </p>
              <p className="mt-3">
                A execução do plano está sujeita a controlo (n.º 4 do artigo 6.º do RGPC) e o
                documento é revisto a cada três anos ou sempre que se opere alteração nas
                atribuições ou na estrutura orgânica da entidade que o justifique. Os relatórios
                anuais de execução são elaborados em abril do ano seguinte e comunicados à Inspeção
                Geral de Finanças e ao Mecanismo Nacional Anticorrupção (MENAC).
              </p>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>2 — Objetivos</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                O que o plano procura assegurar
              </h2>
              <ul className="mt-3 ml-5 list-outside list-disc space-y-2">
                <li>
                  Identificar as áreas de risco de corrupção e infrações conexas em cada área de
                  atividade.
                </li>
                <li>
                  Estabelecer medidas preventivas e, sempre que necessário, corretivas, destinadas a
                  reduzir a probabilidade de ocorrência de corrupção e infrações conexas.
                </li>
                <li>Definir e identificar os responsáveis pela implementação e gestão do plano.</li>
                <li>
                  Promover uma cultura de integridade, ética e responsabilidade no exercício de
                  funções públicas.
                </li>
                <li>
                  Reforçar a transparência e a confiança dos cidadãos na atuação da Junta de
                  Freguesia.
                </li>
              </ul>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>3 — Âmbito</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                A quem se aplica
              </h2>
              <p>
                O plano abrange todas as áreas de atividade da {name} e aplica-se aos membros do
                órgão executivo, a todos os trabalhadores e demais colaboradores, bem como aos
                fornecedores de bens e prestadores de serviços.
              </p>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>4 — Compromisso Ético</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Princípios e valores que orientam a ação
              </h2>
              <p>
                A ética da Administração Pública assenta na Carta Ética da Administração Pública. A
                Junta assume os seguintes compromissos no exercício das suas funções:
              </p>
              <ul className="mt-3 ml-5 list-outside list-disc space-y-2">
                <li>
                  Integridade, na procura das melhores soluções para o interesse público, e
                  comportamento profissional irrepreensível.
                </li>
                <li>
                  Respeito absoluto pelo quadro legal vigente e cumprimento das orientações internas
                  e disposições regulamentares.
                </li>
                <li>Manutenção da mais estrita isenção e objetividade.</li>
                <li>Transparência na tomada de decisões e na difusão da informação.</li>
                <li>Publicitação das deliberações executivas e das decisões dos órgãos.</li>
                <li>Igualdade de tratamento e não discriminação.</li>
                <li>
                  Declaração de qualquer presente ou benefício que possa influenciar a
                  imparcialidade no exercício de funções, e não exercício de atividades externas que
                  possam criar situações de conflito de interesses.
                </li>
              </ul>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>5 — Responsáveis</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Quem responde pela execução
              </h2>
              <p>
                A gestão de riscos é responsabilidade dos autarcas e dos demais colaboradores da
                Junta, nos seus diferentes níveis. Quando exista designação formal no âmbito do
                RGPC, a coordenação do programa de cumprimento normativo é assegurada pela pessoa ou
                equipa responsável, com garantias de independência, meios adequados ao exercício das
                tarefas e acesso à informação interna necessária.
              </p>
              <p className="mt-3">A essa pessoa ou equipa compete, designadamente:</p>
              <ul className="mt-3 ml-5 list-outside list-disc space-y-2">
                <li>
                  Coordenar o levantamento dos riscos de corrupção e infrações conexas, a respetiva
                  análise e a identificação de medidas preventivas, garantindo o envolvimento de
                  toda a estrutura.
                </li>
                <li>
                  Coordenar o levantamento e sistematização dos valores éticos e princípios de ação,
                  promovendo a participação no Código de Conduta.
                </li>
                <li>
                  Assegurar o cumprimento dos prazos de comunicação, divulgação e publicitação do
                  Código de Conduta, do Plano de Prevenção de Riscos e dos respetivos relatórios.
                </li>
                <li>
                  Acompanhar o funcionamento do Canal de Denúncias, incluindo as garantias de
                  proteção dos denunciantes e o cumprimento dos deveres de confidencialidade.
                </li>
                <li>
                  Identificar necessidades formativas em ética, integridade e prevenção de riscos.
                </li>
              </ul>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>6 — Mapa de Riscos e Medidas de Prevenção</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Áreas de atividade abrangidas
              </h2>
              <p>
                O plano identifica os riscos por área, classifica-os em função do grau de
                probabilidade de ocorrência (<em>Elevado</em>, <em>Moderado</em> ou <em>Fraco</em>)
                e associa-lhes medidas preventivas. As cinco áreas analisadas são:
              </p>
              <ul className="mt-3 ml-5 list-outside list-disc space-y-2">
                <li>
                  <strong className="text-on-surface">
                    Atendimento ao público, arquivo e serviços gerais
                  </strong>
                  : princípios da atividade administrativa, isenção no atendimento, gestão
                  documental.
                </li>
                <li>
                  <strong className="text-on-surface">Recursos humanos</strong>: recrutamento,
                  processamento de remunerações e ajudas de custo, avaliação SIADAP.
                </li>
                <li>
                  <strong className="text-on-surface">Contratação pública</strong>: procedimentos
                  pré-contratuais, celebração e execução de contratos, prevenção de conflitos de
                  interesses e situações de conluio.
                </li>
                <li>
                  <strong className="text-on-surface">Concessão de benefícios públicos</strong>:
                  atribuição de subsídios a entidades e associações, verificação da utilização dos
                  apoios e prevenção de favoritismo.
                </li>
                <li>
                  <strong className="text-on-surface">Gestão financeira</strong>: receita, despesa e
                  aprovisionamento, cumprimento do SNC-AP e da Norma de Controlo Interno.
                </li>
              </ul>
              <p className="mt-4 text-sm">
                A tabela completa, com a listagem detalhada dos riscos identificados, do grau de
                probabilidade de ocorrência e das medidas preventivas associadas a cada área, consta
                do{' '}
                <a
                  href={PDF_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackUmamiEvent('document_download', {
                      document_type: 'risk_prevention_plan',
                      source: 'inline_link',
                    })
                  }
                  className="text-primary font-medium hover:underline"
                >
                  Anexo 01 do documento integral
                </a>
                .
              </p>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>7 — Controlo e Monitorização</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Acompanhamento contínuo
              </h2>
              <p>
                A coordenação das atividades de implementação, execução, monitorização e elaboração
                de relatórios cabe ao Órgão Executivo. No final de cada ano civil é elaborado e
                submetido a aprovação um relatório com a avaliação das medidas implementadas, o grau
                de execução e, sempre que se revele necessário, proposta de novas medidas e
                respetivo cronograma.
              </p>
              <p className="mt-3">
                Dada a reduzida dimensão da autarquia, não existe serviço de auditoria interna; a
                fiscalização institucional compete à Assembleia de Freguesia, nos termos legais.
              </p>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>8 — Formação e Sensibilização</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Programas de integridade
              </h2>
              <p>
                No âmbito da execução do plano são realizadas ações de formação e sensibilização
                para responsáveis e trabalhadores sobre corrupção e infrações conexas, fomentando
                uma cultura de transparência administrativa, compromisso e responsabilização. Os
                programas de formação articulam três componentes complementares:
              </p>
              <ul className="mt-3 ml-5 list-outside list-disc space-y-2">
                <li>
                  <strong className="text-on-surface">Comportamental</strong>: abordagem da ausência
                  de integridade, dos conflitos de interesses e das práticas de corrupção e
                  infrações conexas.
                </li>
                <li>
                  <strong className="text-on-surface">Normativa</strong>: apresentação das normas
                  previstas na legislação e das medidas internas de prevenção.
                </li>
                <li>
                  <strong className="text-on-surface">Trabalho em grupo</strong>: dinâmicas de
                  reflexão e debate sobre dilemas éticos que possam surgir no desempenho concreto
                  das funções.
                </li>
              </ul>
              <p className="mt-3">
                Anualmente é feito o levantamento de necessidades formativas, com particular atenção
                a áreas funcionais mais expostas: contratação pública, gestão de património,
                recursos humanos e gestão de informação.
              </p>
            </section>

            <hr className="border-line" />

            <section>
              <span className={sectionLabel}>9 — Divulgação do Plano</span>
              <h2 className="font-headline text-on-surface mb-3 text-xl font-bold">
                Acesso ao documento integral
              </h2>
              <p>
                O presente site é o principal veículo de divulgação. Aqui são disponibilizados o
                plano, o mapa de riscos e, quando aprovados e disponíveis, os relatórios anuais
                aplicáveis. A versão integral do plano está disponível para consulta e descarga{' '}
                <a
                  href={PDF_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackUmamiEvent('document_download', {
                      document_type: 'risk_prevention_plan',
                      source: 'disclosure_link',
                    })
                  }
                  className="text-primary font-medium hover:underline"
                >
                  nesta ligação (PDF)
                </a>
                .
              </p>
              <p className="mt-3">
                Restante documentação de transparência pode ser consultada na secção{' '}
                <Link href="/documents" className="text-primary font-medium hover:underline">
                  Documentos
                </Link>{' '}
                deste site. Para esclarecimentos adicionais, contacte-nos através da página de{' '}
                <Link href="/contact" className="text-primary font-medium hover:underline">
                  Contactos
                </Link>
                .
              </p>
            </section>
          </article>
        </div>
      </div>
    </>
  );
}
